"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
  ChevronLeft,
  Calendar,
  ShieldCheck,
  Info,
  PenToolIcon,
  Truck,
  BadgeCheck,
  Clock,
  Tag,
  CheckCircle2,
  Box,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { EquipmentWithRelations } from "@/store/equipment/equipment-store";
import { EquipmentGallery } from "@/components/equipment/detail/EquipmentGallery";
import { EquipmentInfo } from "@/components/equipment/detail/EquipmentInfo";
import { EquipmentPricing } from "@/components/equipment/detail/EquipmentPricing";
import { EquipmentFeatures } from "@/components/equipment/detail/EquipmentFeatures";
import EquipmentFAQ from "@/components/equipment/detail/EquipmentFAQ";
import EquipmentHowItWorks from "@/components/equipment/detail/EquipmentHowItWorks";
import EquipmentWhyChooseUs from "@/components/equipment/detail/EquipmentWhyChooseUs";
import { EquipmentSpecialDiscounts } from "@/components/equipment/detail/EquipmentSpecialDiscounts";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { HireButton, FloatingHireButton } from "@/components/hire/hire-button";
import ContactFormDrawer from "@/components/ad/ContactFormDrawer";

interface EquipmentDetailProps {
  equipment: EquipmentWithRelations;
  isLoading?: boolean;
}

function DescriptionText({ text }: { text: string }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const lines = text.split("\n");
  const shouldTruncate = lines.length > 5;
  const displayText =
    shouldTruncate && !isExpanded ? lines.slice(0, 5).join("\n") : text;

  return (
    <div className="text-black">
      <div className="whitespace-pre-line text-sm leading-relaxed">
        {displayText}
      </div>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 text-[var(--color-rr-green)] text-sm font-medium hover:underline"
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
}

export function EquipmentDetail({
  equipment,
  isLoading = false,
}: EquipmentDetailProps) {
  const [isActionBarExpanded, setIsActionBarExpanded] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  // Check if equipment is available for hire
  const isAvailableForHire = equipment.isAvailableForHire !== false;
  
  // Debug log to verify field is available
  console.log('Equipment availability check:', {
    equipmentName: equipment.name,
    isAvailableForHire: equipment.isAvailableForHire,
    calculated: isAvailableForHire
  });

  if (isLoading) {
    return <EquipmentDetailSkeleton />;
  }

  if (!equipment) {
    return <EquipmentNotFound />;
  }

  return (
    <div className="container mx-auto py-8">
      {/* Back button with fancy hover effect */}
      <Button
        variant="ghost"
        asChild
        className="mb-6 group relative overflow-hidden transition-all hover:pl-7"
      >
        <Link href="/equipment" className="flex items-center">
          <ChevronLeft className="mr-2 h-4 w-4 transition-transform group-hover:scale-125" />
          <span className="relative z-10">Back to Equipment</span>
          <span className="absolute inset-0 w-0 bg-[var(--color-rr-light-green)] opacity-10 transition-all group-hover:w-full"></span>
        </Link>
      </Button>

      {/* Product Header - Category & Status badges */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="bg-white border-[var(--color-rr-light-green)] text-[var(--color-rr-forest-green)]"
          >
            <Tag className="h-3.5 w-3.5 mr-1 text-[var(--color-rr-green)]" />
            {equipment.category.name}
          </Badge>

          {equipment.condition && (
            <Badge variant="outline" className="bg-white">
              <Info className="h-3.5 w-3.5 mr-1 text-[var(--color-rr-forest-green)]" />
              {equipment.condition}
            </Badge>
          )}

          {equipment.instantBooking ? (
            <Badge className="bg-[var(--color-rr-green)]">
              <Clock className="h-3.5 w-3.5 mr-1" />
              Instant Booking
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-[var(--color-rr-gold)]">
              <Calendar className="h-3.5 w-3.5 mr-1" />
              Booking Request Required
            </Badge>
          )}
        </div>

        {equipment.isFeatured && (
          <Badge className="bg-[var(--color-rr-gold)] text-white">
            <BadgeCheck className="h-3.5 w-3.5 mr-1" />
            Featured
          </Badge>
        )}
      </div>

      {/* Main Product Card */}
      <Card className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow mb-8">
        <CardContent className="p-6">
          {/* Equipment Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            {equipment.name}
          </h1>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Left column - Gallery */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 rounded-xl overflow-hidden">
                <EquipmentGallery
                  primaryImage={equipment.primaryImage}
                  galleryImages={equipment.galleryImages || []}
                  name={equipment.name}
                />
              </div>
            </div>

            {/* Right column - Pricing & Quick Hire */}
            <div className="space-y-6">
              {/* Pricing Section - Plain */}
              <div>
                <EquipmentPricing
                  priceBandScheme={equipment.priceBandScheme}
                />
              </div>

              {/* Equipment Details - Minimal */}
              {(equipment.makeAndModel || equipment.serialNumber) && (
                <div className="grid grid-cols-2 gap-4 pt-4">
                  {equipment.makeAndModel && (
                    <div>
                      <p className="text-xs text-gray-500">Make & Model</p>
                      <p className="text-sm font-medium">
                        {equipment.makeAndModel}
                      </p>
                    </div>
                  )}

                  {equipment.serialNumber && (
                    <div>
                      <p className="text-xs text-gray-500">Serial Number</p>
                      <p className="text-sm font-medium">
                        {equipment.serialNumber}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Get Rental Estimate Section */}
              <div className="pt-4 space-y-3">
                {isAvailableForHire ? (
                  <>
                    <HireButton
                      equipment={{
                        id: equipment.id,
                        name: equipment.name,
                        slug: equipment.slug,
                        primaryImage: equipment.primaryImage,
                        dailyPrice: equipment.dailyPrice,
                        weeklyPrice: equipment.weeklyPrice,
                      }}
                      variant="primary"
                      size="lg"
                      className="w-full font-semibold bg-green-800 hover:bg-green-900 text-white transition-all duration-300 group"
                    >
                      Calculate Rental Now
                      <svg
                        className="w-5 h-5 ml-2"
                        style={{
                          animation: "moveLeftRight 1.5s ease-in-out infinite",
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                      <style jsx>{`
                        @keyframes moveLeftRight {
                          0%,
                          100% {
                            transform: translateX(0px);
                          }
                          50% {
                            transform: translateX(8px);
                          }
                        }
                      `}</style>
                    </HireButton>

                    {/* OR Divider */}
                    <div className="flex items-center">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="px-3 text-sm text-gray-500 bg-white">
                        or
                      </span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>

                    {/* Call to Book Button */}
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full font-semibold !bg-white !border-2 !border-green-800 text-green-800 hover:!bg-green-50 transition-all duration-300"
                      asChild
                    >
                      <a
                        href="tel:0477746329"
                        className="flex items-center justify-center"
                      >
                        <Phone className="w-5 h-5 mr-2" />
                        Call to Book: 04 77 746 329
                      </a>
                    </Button>
                  </>
                ) : (
                  /* Not Available Message */
                  <div className="text-center py-8 bg-orange-50 border-2 border-orange-200 rounded-lg">
                    <div className="mb-4">
                      <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-200 text-lg px-4 py-2">
                        Not Available for Booking
                      </Badge>
                    </div>
                    <p className="text-gray-600 mb-4">
                      This equipment is currently not available for hire.
                    </p>
                    <p className="text-sm text-gray-500">
                      Please check back later or contact us for more information.
                    </p>
                    
                    {/* Contact Options */}
                    <div className="mt-6 space-y-3">
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full font-semibold !bg-white !border-2 !border-orange-600 text-orange-600 hover:!bg-orange-50 transition-all duration-300"
                        onClick={() => setIsContactFormOpen(true)}
                      >
                        <PenToolIcon className="w-5 h-5 mr-2" />
                        Send Message
                      </Button>

                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full font-semibold !bg-white !border-2 !border-orange-600 text-orange-600 hover:!bg-orange-50 transition-all duration-300"
                        asChild
                      >
                        <a
                          href="tel:0477746329"
                          className="flex items-center justify-center"
                        >
                          <Phone className="w-5 h-5 mr-2" />
                          Call to Book: 04 77 746 329
                        </a>
                      </Button>
                    </div>
                  </div>
                )}

                {/* Show contact form only when equipment is available */}
                {isAvailableForHire && (
                  <>
                    {/* OR Divider */}
                    <div className="flex items-center">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="px-3 text-sm text-gray-500 bg-white">
                        or
                      </span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>

                    {/* Contact Form Button */}
                    <Button
                  variant="outline"
                  size="lg"
                  className="w-full font-semibold !bg-white !border-2 !border-green-800 text-green-800 hover:!bg-green-50 transition-all duration-300"
                  onClick={() => setIsContactFormOpen(true)}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Send Message & Get Quote
                </Button>
                  </>
                )}
              </div>

              {/* Equipment Info */}
              <div className="pt-4 space-y-2">
                <div className="flex items-center space-x-2">
                  <ShieldCheck className="h-4 w-4 text-gray-600" />
                  <p className="text-xs text-gray-600">
                    Insurance included • $1000 excess
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Truck className="h-4 w-4 text-gray-600" />
                  <p className="text-xs text-gray-600">
                    Delivery $50 • Pickup $50
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compact Floating Action Bar */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 md:left-16 md:bottom-8 md:translate-x-0 z-50">
        <div
          className={`flex items-center bg-green-600 rounded-full shadow-lg p-1 transition-all duration-300 ${
            isActionBarExpanded ? "md:w-auto" : "md:w-fit"
          }`}
        >
          {/* Mobile Layout - Always Compact */}
          <div className="flex items-center md:hidden">
            {/* Call Button Circle */}
            <a
              href="tel:0477746329"
              className="bg-white hover:bg-gray-100 text-green-800 w-12 h-12 rounded-full transition-colors duration-300 flex items-center justify-center flex-shrink-0"
            >
              <Phone className="w-5 h-5" />
            </a>

            {/* White Separator */}
            <div className="w-px h-6 bg-white mx-2 flex-shrink-0"></div>

            {/* Hire Now Button */}
            <HireButton
              equipment={{
                id: equipment.id,
                name: equipment.name,
                slug: equipment.slug,
                primaryImage: equipment.primaryImage,
                dailyPrice: equipment.dailyPrice,
                weeklyPrice: equipment.weeklyPrice,
              }}
              variant="primary"
              size="sm"
              className="bg-green-800 hover:bg-green-900 text-white px-4 py-3 font-medium border border-white !rounded-full"
            >
              Hire Now
            </HireButton>
          </div>

          {/* Desktop Layout - Compact/Expandable */}
          <div className="hidden md:flex items-center">
            {/* Compact State - Just Icons */}
            {!isActionBarExpanded && (
              <>
                {/* Call Button Circle - Clickable to expand */}
                <button
                  onClick={() => setIsActionBarExpanded(true)}
                  className="bg-white hover:bg-gray-100 text-green-800 w-12 h-12 rounded-full transition-colors duration-300 flex items-center justify-center flex-shrink-0"
                >
                  <Phone className="w-5 h-5" />
                </button>

                {/* White Separator */}
                <div className="w-px h-6 bg-white mx-2 flex-shrink-0"></div>

                {/* Hire Button - Compact */}
                <HireButton
                  equipment={{
                    id: equipment.id,
                    name: equipment.name,
                    slug: equipment.slug,
                    primaryImage: equipment.primaryImage,
                    dailyPrice: equipment.dailyPrice,
                    weeklyPrice: equipment.weeklyPrice,
                  }}
                  variant="primary"
                  size="sm"
                  className="bg-green-800 hover:bg-green-900 text-white px-4 py-3 font-medium border border-white !rounded-full"
                >
                  Hire Now
                </HireButton>
              </>
            )}

            {/* Expanded State - With Phone Number */}
            {isActionBarExpanded && (
              <>
                {/* Call Button Circle - Clickable link */}
                <a
                  href="tel:0477746329"
                  className="bg-white hover:bg-gray-100 text-green-800 w-12 h-12 rounded-full transition-colors duration-300 flex items-center justify-center flex-shrink-0"
                >
                  <Phone className="w-5 h-5" />
                </a>

                {/* White Separator */}
                <div className="w-px h-6 bg-white mx-2 flex-shrink-0"></div>

                {/* Phone Number Display */}
                <div className="text-white px-3 py-3 font-medium">
                  <span className="text-lg font-bold whitespace-nowrap">
                    04 77 746 329
                  </span>
                </div>

                {/* Separator */}
                <div className="w-px h-6 bg-white mx-1 flex-shrink-0"></div>

                {/* Hire Button */}
                <HireButton
                  equipment={{
                    id: equipment.id,
                    name: equipment.name,
                    slug: equipment.slug,
                    primaryImage: equipment.primaryImage,
                    dailyPrice: equipment.dailyPrice,
                    weeklyPrice: equipment.weeklyPrice,
                  }}
                  variant="primary"
                  size="sm"
                  className="bg-green-800 hover:bg-green-900 text-white px-4 py-3 font-medium border border-white !rounded-full"
                >
                  Hire Now
                </HireButton>

                {/* Collapse Button */}
                <button
                  onClick={() => setIsActionBarExpanded(false)}
                  className="bg-white hover:bg-gray-100 text-green-800 w-8 h-8 rounded-full transition-colors duration-300 flex items-center justify-center flex-shrink-0 ml-2"
                >
                  <span className="text-sm">×</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Original Floating mobile button (hidden) */}
      <div className="hidden">
        <FloatingHireButton
          equipment={{
            id: equipment.id,
            name: equipment.name,
            slug: equipment.slug,
            primaryImage: equipment.primaryImage,
            dailyPrice: equipment.dailyPrice,
            weeklyPrice: equipment.weeklyPrice,
          }}
        />
      </div>

      {/* Equipment quick specs */}
      {equipment.quantity > 1 && (
        <div className="py-8">
          <Card className="bg-gradient-to-br from-white to-gray-50 border-none shadow-sm">
            <CardContent className="p-6">
              <div className="flex justify-center">
                <div className="flex flex-col items-center text-center p-3 bg-white rounded-lg shadow-sm">
                  <Truck className="h-8 w-8 text-[var(--color-rr-green)] mb-2" />
                  <h3 className="text-xs font-medium text-gray-500">
                    Available Units
                  </h3>
                  <p className="text-sm font-semibold mt-1">
                    {equipment.quantity}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Equipment details */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-1 pb-8">
        {/* Equipment info */}
        <div className="space-y-6">
          {/* Special Rental Discounts Section */}
          <EquipmentSpecialDiscounts priceBandScheme={equipment.priceBandScheme} />

          {/* Description card with modern design */}
          <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow p-2 md:p-8">
            <CardContent className="pt-6">
              <h2 className="text-xl font-bold mb-4 flex items-center text-black">
                <Info className="h-5 w-5 mr-2 text-[var(--color-rr-green)]" />
                Description
              </h2>

              <DescriptionText text={equipment.description} />
            </CardContent>
          </Card>

          {/* Why Choose Us Section */}
          <EquipmentWhyChooseUs />

          {/* How It Works Section */}
          <EquipmentHowItWorks />

          {/* FAQ Section */}
          <EquipmentFAQ />
        </div>
      </div>

      {/* Contact Form Drawer */}
      <ContactFormDrawer
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
        phoneNumber="0477746329"
      />
    </div>
  );
}

// Equipment not found component
function EquipmentNotFound() {
  return (
    <div className="container mx-auto py-8">
      <div className="bg-muted rounded-lg p-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Equipment Not Found</h1>
        <p className="mb-6">
          The equipment you're looking for could not be found.
        </p>
        <Button asChild>
          <Link href="/equipment">
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back to Equipment
          </Link>
        </Button>
      </div>
    </div>
  );
}

// Skeleton loader for the detail page
function EquipmentDetailSkeleton() {
  return (
    <div className="container mx-auto py-8">
      {/* Back button skeleton */}
      <div className="mb-6">
        <Skeleton className="h-10 w-40" />
      </div>

      {/* Header badges skeleton */}
      <div className="flex gap-2 mb-4">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-32" />
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Left column - Gallery skeleton */}
        <div className="lg:col-span-2">
          <Skeleton className="h-[400px] w-full rounded-xl" />

          {/* Thumbnail skeletons */}
          <div className="flex gap-2 mt-2">
            <Skeleton className="h-16 w-16" />
            <Skeleton className="h-16 w-16" />
            <Skeleton className="h-16 w-16" />
          </div>
        </div>

        {/* Right column - Info & Booking skeleton */}
        <div className="space-y-8">
          <Skeleton className="h-[200px] w-full rounded-xl" />
          <Skeleton className="h-[300px] w-full rounded-xl" />
        </div>
      </div>

      <div className="py-8">
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>

      {/* Equipment details skeleton */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Left column - Basic info skeleton */}
        <div className="lg:col-span-2 space-y-6">
          <Skeleton className="h-[200px] w-full rounded-xl" />
          <Skeleton className="h-[300px] w-full rounded-xl" />
        </div>

        {/* Right column - Features skeleton */}
        <div>
          <Skeleton className="h-[400px] w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}
